import {
  Box,
  Grid,
  Stack,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReplyIcon from "@mui/icons-material/Reply";
import SendIcon from "@mui/icons-material/Send";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { CommonButton } from "../common/commonButton";
import { useEffect, useState } from "react";
import { useStyles } from "../bankTransactions/bankTranscationContainerStyles";
import { CommonPaginationContainer } from "../common";
import { getPaginationCount } from "../CashAllocation/utils";
import { GetEmailList, SendEmail } from "../../Services/api";
import instance from "../../redux/api";
import { toast } from "react-toastify";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { CommonFilterSidebar } from "../common/CommonFilterSidebar";
import "../common/commonPageStyles.css";

export default function EmailContainer() {
  const toggle = useSelector((state) => state.toggleSideMenu);
  const classes = useStyles();

  //For Date picker
  const [fromDateValue, setFromDateValue] = useState("");
  const [toDateValue, setToDateValue] = useState("");
  const [fromNameValue, setFromNameValue] = useState("");
  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [isClearPerformed, setIsClearPerformed] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isReplyEnable, setIsReplyEnable] = useState(false);
  const [emailReply, setEmailReply] = useState(null);
  const [isSendingEmailReply, setIsSendingEmailReply] = useState(false);

  // Filter sidebar state
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);

  const isFilterBtnDisable = () => {
    let disabled = true;
    if (fromDateValue !== "" || toDateValue !== "" || (fromNameValue || "") !== "") {
      disabled = false;
    }
    return disabled;
  };

  const handleClearSearchEmailList = async () => {
    setIsClearPerformed(true);
    setFromDateValue("");
    setToDateValue("");
    setFromNameValue("");
    setPageState((old) => ({
      ...old,
      page: 1,
    }));
  };

  //For pagination
  const [paginationCount, setPaginationCount] = useState(0);
  const [pageState, setPageState] = useState({
    isLoading: true,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
  });

  // skip 0 means page 1
  const currentSkipNumber = () => {
    return pageState?.page === 1
      ? 0
      : pageState?.page === 0
        ? pageState?.page
        : pageState?.page - 1;
  };

  const loadData = async () => {
    try {
      setPageState((old) => ({
        ...old,
        isLoading: true,
      }));

      let queryParams = `skip=${currentSkipNumber()}&pageSize=${pageState?.pageSize}`;

      if (isSearchPerformed) {
        const getSearchQueryParam = searchQueryParams();
        if (getSearchQueryParam) {
          queryParams = `${queryParams}${getSearchQueryParam}`;
        }
      }

      const response = await instance.get(`${GetEmailList}?${queryParams}`);

      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));

      handleVarianceResponse(response);
    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.error("err", err);
    }
  };

  const handleVarianceResponse = (response) => {
    if (response.status === 200) {
      setPageState((old) => ({
        ...old,
        total: response?.data?.count,
        data: response.data.data || [],
      }));
      setPaginationCount(
        getPaginationCount(response?.data?.count, pageState?.pageSize)
      );
    } else {
      setPageState((old) => ({
        ...old,
        total: response?.data?.count,
        data: [],
      }));
      setPaginationCount(0);
    }
  };

  useEffect(() => {
    loadData();
  }, [pageState?.pageSize, pageState?.page]);

  useEffect(() => {
    if (isClearPerformed) {
      loadData();
    }
  }, [pageState?.pageSize, pageState?.page, isClearPerformed]);

  useEffect(() => {
    setPaginationCount(
      getPaginationCount(pageState?.total, pageState?.pageSize)
    );
  }, [pageState?.total]);

  const searchQueryParams = () => {
    let params = "";

    // handle date values (may be Date objects or strings)
    if (fromDateValue) {
      try {
        const fromDateStr =
          typeof fromDateValue === "string"
            ? fromDateValue
            : fromDateValue.toISOString();
        params += `&fromDate=${encodeURIComponent(fromDateStr)}`;
      } catch (e) {
        // fallback to string
        params += `&fromDate=${encodeURIComponent(String(fromDateValue))}`;
      }
    }

    if (toDateValue) {
      try {
        const toDateStr =
          typeof toDateValue === "string" ? toDateValue : toDateValue.toISOString();
        params += `&toDate=${encodeURIComponent(toDateStr)}`;
      } catch (e) {
        params += `&toDate=${encodeURIComponent(String(toDateValue))}`;
      }
    }

    if (fromNameValue && String(fromNameValue).trim() !== "") {
      params += `&fromName=${encodeURIComponent(String(fromNameValue).trim())}`;
    }

    return params;
  };

  const handleSearchEmailList = async () => {
    try {
      setPageState((old) => ({
        ...old,
        isLoading: true,
        page: 1,
      }));

      let queryParams = `skip=0&pageSize=${pageState?.pageSize}`;
      const getSearchQueryParam = searchQueryParams();
      if (getSearchQueryParam) {
        queryParams = `${queryParams}${getSearchQueryParam}`;
      }

      const response = await instance.get(`${GetEmailList}?${queryParams}`);

      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));

      if (response.status === 200) {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: response.data.data || [],
          page: 1,
        }));
        setIsSearchPerformed(true);
        setIsClearPerformed(false);
        setPaginationCount(
          getPaginationCount(response?.data?.count, pageState?.pageSize)
        );
      } else {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: [],
        }));
        setPaginationCount(0);
      }
    } catch (error) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.error("error", error);
    }
  };

  const handleEnter = (e) => {
    if (e.code === "Enter") {
      handleSearchEmailList();
    }
  };

  const handleBackClick = () => {
    if (isReplyEnable) {
      setIsReplyEnable(false);
    } else {
      setSelectedEmail(null);
      setIsEmailOpen(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedEmail(null);
    setIsEmailOpen(false);
    setIsReplyEnable(false);
    setEmailReply(null);
  };

  const HandleReplyClick = () => {
    setIsReplyEnable(true);
  };

  const handleReplyChange = (reply) => {
    console.log("reply", reply);
    setEmailReply(reply);
  };

  const HandleSendClick = async () => {
    setIsSendingEmailReply(true);
    try {
      console.log("emailReply", emailReply);
      const payload = selectedEmail;
      payload.reply = emailReply;
      let response = await instance.post(SendEmail, payload);
      console.log("response", response);
      if (response?.status === 200) {
        toast.success("Email send successfully!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });

        setIsReplyEnable(false);
        setEmailReply(null);
        loadData();
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
    } finally {
      setIsSendingEmailReply(false);
    }
  };

  const columns = [
    {
      field: "from",
      headerName: "From",
      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params?.row?.from,
      renderCell: (params) => (
        <span
          style={{
            color: "#000",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",  
            height: "100%",
          }}
        >
          {params?.row?.from}
        </span>
      ),
    },
    {
      field: "subject",
      headerName: "Subject",
      align: "left",
      headerAlign: "center",
      flex: 2,
      minWidth: 300,
      valueGetter: (params) => params?.row?.subject,
      renderCell: (params) => (
        <a
          href={`javascript:void(0)`}
          onClick={() => {
            setIsEmailOpen(true);
            setSelectedEmail(params?.row);
          }}
          rel="noopener noreferrer"
          role="button"
          style={{
            color: "#1672DE",
            textAlign: "left",
            textDecoration: "none",
            alignItems: "center",
            width: "100%",
            paddingLeft: "5px",
            fontSize: "14px",
          }}
        >
          <span style={{ color: "#1672DE" }}>{params?.row?.subject}</span>
        </a>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      align: "center",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params?.row?.date,
      renderCell: (params) => (
        <span style={{ color: "#000", fontSize: "14px", textAlign: "center", width: "100%", display: "block" }}>
          {params?.row?.date}
        </span>
      ),
    },
  ];

  const Container = {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 0,
    height: "calc(100vh - 180px)",
    maxHeight: "calc(100vh - 180px)",
    overflow: "hidden",
  };

  return (
    <Box sx={Container}>
      <Dialog
        open={isEmailOpen && selectedEmail !== null}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#1672DE",
            color: "white",
            padding: "20px 24px",
            fontWeight: "600",
            fontSize: "20px",
            textAlign: "center",
            position: "relative",
          }}
        >
          Email Details
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "0",
            overflow: "auto",
            flex: 1,
            backgroundColor: "#f8f9fa",
          }}
        >
          {isReplyEnable ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Reply Header */}
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  padding: "24px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Box
                  sx={{
                  display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: "16px",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "12px" }}>
                      <PersonIcon sx={{ fontSize: "18px", color: "#1672DE" }} />
                      <Typography sx={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                        From
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "15px", fontWeight: "500", color: "#333", ml: "26px" }}>
                      {selectedEmail?.to} <Chip label="me" size="small" sx={{ ml: 1, height: "20px", fontSize: "11px", backgroundColor: "#e3f2fd", color: "#1672DE" }} />
                    </Typography>
                  </Box>
                  <Box>
                    {isSendingEmailReply ? (
                      <CircularProgress size={35} sx={{ color: "#1672DE" }} />
                    ) : (
                      <CommonButton
                        text={
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <SendIcon sx={{ fontSize: "18px" }} />
                            <span>Send</span>
                          </Box>
                        }
                        handleClick={HandleSendClick}
                        hoverColor="#1565c0"
                    style={{
                          backgroundColor: "#1672DE",
                          color: "white",
                          borderRadius: "8px",
                          padding: "10px 20px",
                          fontWeight: "500",
                          textTransform: "none",
                          boxShadow: "0 2px 8px rgba(22, 114, 222, 0.3)",
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "12px" }}>
                  <EmailIcon sx={{ fontSize: "18px", color: "#1672DE" }} />
                  <Typography sx={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    To
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "15px", fontWeight: "500", color: "#333", ml: "26px", mb: "16px" }}>
                  {selectedEmail?.from}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "8px" }}>
                  <EmailIcon sx={{ fontSize: "18px", color: "#1672DE" }} />
                  <Typography sx={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    Subject
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "16px", fontWeight: "600", color: "#333", ml: "26px" }}>
                  Re: {selectedEmail?.subject}
                </Typography>
              </Box>
              
              {/* Reply Text Area */}
              <Box sx={{ flex: 1, padding: "24px", backgroundColor: "#ffffff" }}>
                <textarea
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: "350px",
                    padding: "16px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "8px",
                    outline: "none",
                    resize: "none",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    lineHeight: "1.6",
                    backgroundColor: "#fafafa",
                    boxSizing: "border-box",
                    color: "#333",
                    transition: "border-color 0.2s ease",
                  }}
                  onChange={(e) => handleReplyChange(e.target.value)}
                  placeholder="Type your reply here..."
                  onFocus={(e) => {
                    e.target.style.borderColor = "#1672DE";
                    e.target.style.backgroundColor = "#ffffff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e0e0e0";
                    e.target.style.backgroundColor = "#fafafa";
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Email Header Card */}
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  padding: "24px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                {/* Subject */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    mb: "20px",
                    padding: "12px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    borderLeft: "4px solid #1672DE",
                  }}
                >
                  <EmailIcon sx={{ fontSize: "20px", color: "#1672DE" }} />
                  <Typography sx={{ fontSize: "18px", fontWeight: "600", color: "#333", flex: 1 }}>
                    {selectedEmail?.subject}
                  </Typography>
                </Box>

                {/* Email Metadata */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                    mb: "20px",
                  }}
                >
                  <Box
                    sx={{
                      padding: "12px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px" }}>
                      <PersonIcon sx={{ fontSize: "16px", color: "#1672DE" }} />
                      <Typography sx={{ fontSize: "12px", color: "#666", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        From
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: "500", color: "#333", ml: "24px" }}>
                      {selectedEmail?.from_full_name} 
                    </Typography>
                  </Box>

                  <Box
              sx={{
                      padding: "12px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px" }}>
                      <PersonIcon sx={{ fontSize: "16px", color: "#1672DE" }} />
                      <Typography sx={{ fontSize: "12px", color: "#666", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        To
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: "500", color: "#333", ml: "24px" }}>
                      {selectedEmail?.to} <Chip label="me" size="small" sx={{ ml: 1, height: "20px", fontSize: "11px", backgroundColor: "#e3f2fd", color: "#1672DE" }} />
                    </Typography>
                  </Box>
                </Box>

                {/* Date and Reply Button */}
                <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                    padding: "12px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <AccessTimeIcon sx={{ fontSize: "16px", color: "#666" }} />
                    <Typography sx={{ fontSize: "13px", color: "#666" }}>
                      {selectedEmail?.date}
                </Typography>
                  </Box>
                  <CommonButton
                    text={
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <ReplyIcon sx={{ fontSize: "18px" }} />
                        <span>Reply</span>
                      </Box>
                    }
                    handleClick={HandleReplyClick}
                    hoverColor="#1565c0"
                    style={{
                      backgroundColor: "#1672DE",
                      color: "white",
                      borderRadius: "8px",
                      padding: "10px 20px",
                      fontWeight: "500",
                      textTransform: "none",
                      boxShadow: "0 2px 8px rgba(22, 114, 222, 0.3)",
                    }}
                  />
                </Box>
              </Box>

              {/* Email Body */}
              <Box
                sx={{
                  flex: 1,
                  padding: "24px",
                  backgroundColor: "#ffffff",
                  overflow: "auto",
                }}
              >
                <Box
                  sx={{
                    padding: "20px",
                    backgroundColor: "#fafafa",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    minHeight: "200px",
                }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: "1.8",
                      color: "#333",
                      whiteSpace: "pre-wrap",
                      fontFamily: "inherit",
                    }}
                  >
                    {selectedEmail?.body}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Box sx={{ display: "flex", flexDirection: "row", height: "100%" }}>
        <CommonFilterSidebar
            isOpen={filterSidebarOpen}
            filterConfigs={[
              {
                type: "dateRange",
                label: "Email Period",
                startDate: fromDateValue,
                setStartDate: setFromDateValue,
                endDate: toDateValue,
                setEndDate: setToDateValue,
                maxDate: new Date(),
                onKeyUp: handleEnter,
              },
              {
                type: "textInput",
                label: "From Name",
                value: fromNameValue,
                onChange: setFromNameValue,
                onKeyUp: handleEnter,
              },
            ]}
            onApplyFilters={handleSearchEmailList}
            onClearFilters={handleClearSearchEmailList}
            isLoading={pageState?.isLoading}
            isFilterDisabled={isFilterBtnDisable()}
          />

          {/* Main Content Area */}
          <Box
            className="main-content-area"
                sx={{
              flex: 1,
                  display: "flex",
              flexDirection: "column",
              minHeight: 0,
                }}
              >
            <Box
              className="page-header-container"
              sx={{
                  display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Typography
                  variant="h5"
                  className="page-heading"
                >
                  Email
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <IconButton
                  onClick={() => setFilterSidebarOpen(!filterSidebarOpen)}
                  className="filter-toggle-button"
              sx={{
                    color: "#1672DE",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    padding: "8px",
                    "&:hover": {
                      backgroundColor: "rgba(22, 114, 222, 0.1)",
                      borderColor: "#1672DE",
                    },
                  }}
                >
                  <FilterListIcon />
                </IconButton>
              </Box>
            </Box>

            <Box
            sx={{
                flex: 1,
              display: "flex",
                flexDirection: "column",
                minHeight: 0,
              position: "relative",
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                overflow: "visible",
                transition: "all 0.3s ease",
                border: "1px solid #e8eaf0",
                paddingBottom: "10px",
                margin: "20px",
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                },
            }}
          >
            <DataGrid
              className={classes.pagination}
              loading={pageState?.isLoading}
              rows={pageState?.data ?? []}
              columns={columns}
              rowCount={pageState?.total}
              keepNonExistentRowsSelected
              disableSelectionOnClick
              pagination
              paginationMode="server"
              page={pageState.page - 1}
              pageSize={pageState?.pageSize ?? 25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageSizeChange={(newPageSize) => {
                return setPageState((old) => {
                    setPaginationCount(
                      getPaginationCount(pageState?.total, newPageSize)
                    );
                  return { ...old, pageSize: newPageSize, page: 1 };
                });
              }}
              sx={{
                  border: "none",
                  flex: 1,
                  minHeight: 0,
                  "& .MuiDataGrid-root": {
                    border: "none",
                    height: "100%",
                  },
                  "& .MuiDataGrid-main": {
                    height: "100%",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#1672DE",
                      borderRadius: "10px",
                      "&:hover": {
                        background: "#1565c0",
                      },
                    },
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e0e0e0",
                    minHeight: "52px !important",
                    maxHeight: "52px !important",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor: "transparent",
                    fontWeight: "600",
                    color: "#FF5A01",
                    fontSize: "14px",
                    padding: "12px 16px",
                    borderBottom: "none",
                    overflow: "visible",
                    justifyContent: "center",
                    "&:focus": {
                      outline: "none",
                    },
                    "&:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: "600",
                      fontSize: "14px",
                      overflow: "visible",
                      textOverflow: "clip",
                      whiteSpace: "normal",
                      lineHeight: "1.4",
                      width: "100%",
                      textAlign: "center",
                    },
                    "& .MuiDataGrid-sortIcon": {
                      color: "#FF5A01",
                    },
                    "& .MuiDataGrid-menuIcon": {
                      color: "#FF5A01",
                    },
                  },
                  "& .MuiDataGrid-row": {
                    transition: "all 0.2s ease",
                    borderBottom: "1px solid #f0f0f0",
                    "&:hover": {
                      backgroundColor: "rgba(255, 90, 1, 0.04)",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: "#fafafa",
                      "&:hover": {
                        backgroundColor: "rgba(255, 90, 1, 0.04)",
                      },
                    },
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #f0f0f0",
                    padding: "12px 16px",
                    fontSize: "13px",
                    color: "#333",
                    display: "flex",
                    alignItems: "center",
                    "&:focus": {
                      outline: "none",
                    },
                    "&:focus-within": {
                      outline: "none",
                    },
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    "&::-webkit-scrollbar": {
                      width: "8px",
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#1672DE",
                      borderRadius: "10px",
                      "&:hover": {
                        background: "#1565c0",
                      },
                    },
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "2px solid #e0e0e0",
                    backgroundColor: "#f8f9fa",
                    minHeight: "48px",
                    maxHeight: "48px",
                    paddingBottom: "8px",
                    paddingTop: "8px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    position: "relative",
                    zIndex: 1,
                    "& .MuiTablePagination-root": {
                      color: "#666",
                      paddingRight: "16px",
                      margin: 0,
                      minHeight: "32px",
                    },
                    "& .MuiTablePagination-selectLabel": {
                      fontSize: "13px",
                      margin: 0,
                    },
                    "& .MuiTablePagination-displayedRows": {
                      fontSize: "13px",
                      margin: 0,
                    },
                    "& .MuiTablePagination-actions": {
                      marginLeft: "8px",
                      "& .MuiIconButton-root": {
                        padding: "4px",
                      },
                    },
                    "& .MuiTablePagination-select": {
                      margin: "0 8px",
                      padding: "4px 8px",
                    },
                    "& .MuiTablePagination-toolbar": {
                      minHeight: "32px",
                      padding: 0,
                    },
                  },
                  "& .MuiDataGrid-toolbarContainer": {
                    padding: "12px 16px",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e0e0e0",
                    "& .MuiButton-root": {
                      color: "#FF5A01",
                      fontSize: "13px",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(255, 90, 1, 0.1)",
                      },
                    },
                  },
                  "& .MuiDataGrid-loadingOverlay": {
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                  },
                }}
                components={{
                  Toolbar: GridToolbarContainer,
                  NoRowsOverlay: () => (
                    <Stack
                      height="100%"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ color: "#999" }}
                    >
                      <Typography variant="body2" sx={{ color: "#999" }}>
                        No Emails
                      </Typography>
                    </Stack>
                  ),
                }}
            />
            <Stack
                sx={{
                position: "absolute",
                  bottom: "16px",
                  right: "20px",
                  zIndex: 10,
                  backgroundColor: "#f8f9fa",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <CommonPaginationContainer
                count={paginationCount + 1}
                pageState={pageState}
                setPageState={setPageState}
              />
            </Stack>
            </Box>
          </Box>
        </Box>
    </Box>
  );
}
